/**
 * LegalDiarySettings — Portal Settings → Legal Diary (A1, Oct 2026).
 * The whole legal process is one versioned document: five sections edited as a draft, diffed against the
 * published version, published with a reason (settings_version++), rolled back from history.
 * Live-on-save is retired — nothing changes until Publish.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Layers, GitBranch, ShieldCheck, SlidersHorizontal, BellRing } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  getLegalProcessSettings, saveLegalProcessDraft, discardLegalProcessDraft, publishLegalProcess, validateLegalProcessBody, listLegalEventTypes,
} from "@/lib/api";
import ProcessPublishBar from "./legal/ProcessPublishBar";
import ProcessStagesSection from "./legal/ProcessStagesSection";
import ProcessTransitionsSection from "./legal/ProcessTransitionsSection";
import ProcessGatesSection from "./legal/ProcessGatesSection";
import ProcessThresholdsSection from "./legal/ProcessThresholdsSection";
import ProcessNotificationsSection from "./legal/ProcessNotificationsSection";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const SECTIONS = [
  { key: "stages", label: "Stages", icon: Layers },
  { key: "transitions", label: "Transitions & timers", icon: GitBranch },
  { key: "gates", label: "Gates", icon: ShieldCheck },
  { key: "thresholds", label: "Thresholds & case rules", icon: SlidersHorizontal },
  { key: "notifications", label: "Notifications & templates", icon: BellRing },
];

export default function LegalDiarySettings() {
  const [data, setData] = useState(null);
  const [body, setBody] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [tab, setTab] = useState("thresholds");

  const load = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([getLegalProcessSettings(), listLegalEventTypes()]);
      setData(s);
      setBody(JSON.parse(JSON.stringify(s.draft.body)));
      setEventTypes(t.types || []);
      setErrors([]);
    } catch (e) {
      toast.error("Failed to load Legal Diary settings: " + errorDetailMessage(e, e.message));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => !!data && JSON.stringify(body) !== JSON.stringify(data.draft.body), [body, data]);

  useEffect(() => {
    if (!dirty || !body) return;
    const t = setTimeout(() => validateLegalProcessBody(body).then((r) => setErrors(r.errors || [])).catch(() => {}), 500);
    return () => clearTimeout(t);
  }, [body, dirty]);

  const save = async () => {
    setSaving(true);
    try {
      const d = await saveLegalProcessDraft(body);
      setData((s) => ({ ...s, draft: d }));
      setBody(JSON.parse(JSON.stringify(d.body)));
      setErrors([]);
      toast.success(`Draft saved — ${d.changes.length} change(s) vs v${d.published_version}`);
    } catch (e) {
      const detail = errorDetailMessage(e, e.message);
      setErrors(String(detail).split("; "));
      toast.error("Draft not saved: " + detail);
    } finally { setSaving(false); }
  };

  const discard = async (silent = false) => {
    if (!silent && !window.confirm("Discard the draft and all unsaved edits? The published settings stay as they are.")) return;
    try { await discardLegalProcessDraft(); } catch { /* nothing to discard */ }
    await load();
    if (!silent) toast.success("Draft discarded");
  };

  const publish = async (reason) => {
    try {
      const r = await publishLegalProcess(reason);
      toast.success(`Published v${r.version} — ${r.changes.length} change(s) now live`);
      await load();
    } catch (e) {
      toast.error("Publish failed: " + errorDetailMessage(e, e.message));
      throw e;
    }
  };

  if (!data || !body) {
    return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const sectionProps = { body, setBody, vocab: data.vocab, eventTypes, canPublish: data.can_publish };
  return (
    <div className="space-y-4" data-testid="legal-diary-settings">
      <ProcessPublishBar published={data.published} draft={data.draft} dirty={dirty} errors={errors} saving={saving} canPublish={data.can_publish}
        onSave={save} onDiscard={discard} onPublish={publish} eventTypes={eventTypes} routeStatuses={(body?.stages || []).filter((s) => !s.ladder)} />
      {!data.can_publish && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="read-only-note">
          Read-only — publishing Legal Diary settings needs the <strong>legal_head</strong> permission (or system configuration).
        </p>
      )}
      {errors.length > 0 && (
        <ul className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 space-y-0.5 list-disc list-inside" data-testid="validation-errors">
          {errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          {SECTIONS.map((s) => (
            <TabsTrigger key={s.key} value={s.key} className="text-xs" data-testid={`process-tab-${s.key}`}><s.icon className="w-3.5 h-3.5 mr-1.5" />{s.label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="stages" className="pt-3"><ProcessStagesSection {...sectionProps} /></TabsContent>
        <TabsContent value="transitions" className="pt-3"><ProcessTransitionsSection {...sectionProps} /></TabsContent>
        <TabsContent value="gates" className="pt-3"><ProcessGatesSection {...sectionProps} /></TabsContent>
        <TabsContent value="thresholds" className="pt-3"><ProcessThresholdsSection {...sectionProps} /></TabsContent>
        <TabsContent value="notifications" className="pt-3"><ProcessNotificationsSection {...sectionProps} /></TabsContent>
      </Tabs>
    </div>
  );
}
